import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct
} from '../services/api';
import {
    CheckCircle,
    Plus,
    Search,
    Loader2,
    Package,
    Edit3,
    Trash2,
    X,
    AlertCircle,
    Check,
    Link
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Products = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({ name: '', defaultPrice: '', sourceUrl: '', image: '', active: true });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getProducts({ limit: 100 }); // Get more for the management list
            setProducts(Array.isArray(data) ? data : data.data || []);
            setError(null);
        } catch (err) {
            setError(t('products.messages.load_failed'));
            showToast('error', t('products.messages.load_failed'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const showToast = (type, message) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const handleOpenAddModal = () => {
        setEditingProduct(null);
        setFormData({ name: '', defaultPrice: '', sourceUrl: '', image: '', active: true });
        setError(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            defaultPrice: product.defaultPrice || '',
            sourceUrl: product.sourceUrl || '',
            image: product.image || '',
            active: product.active ?? true
        });
        setError(null);
        setIsModalOpen(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            let res;
            if (editingProduct) {
                res = await updateProduct(editingProduct._id, formData);
                if (res.success) {
                    showToast('success', t('products.messages.update_success'));
                }
            } else {
                res = await createProduct(formData);
                if (res.success) {
                    showToast('success', t('products.messages.create_success'));
                }
            }
            if (res.success) {
                fetchProducts();
                setIsModalOpen(false);
            }
        } catch (err) {
            const msg = err.response?.data?.message || t('users.messages.operation_failed');
            setError(msg);
            showToast('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteProduct = async (id) => {
        if (!window.confirm(t('products.messages.confirm_delete'))) return;
        try {
            await deleteProduct(id);
            showToast('success', t('products.messages.delete_success'));
            fetchProducts();
        } catch (err) {
            showToast('error', t('products.messages.delete_failed'));
        }
    };

    const toggleStatus = async (product) => {
        try {
            await updateProduct(product._id, { active: !product.active });
            showToast('success', !product.active ? t('products.messages.activated') : t('products.messages.deactivated'));
            fetchProducts();
        } catch (err) {
            showToast('error', t('products.messages.status_update_failed'));
        }
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.slug && p.slug.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="px-4 py-6 md:p-8 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
                        {t('products.title')} {user?.brandName && <span className="text-indigo-600 md:inline">— {user.brandName}</span>}
                    </h1>
                    <p className="text-xs md:text-sm text-gray-500 font-medium mt-1 uppercase tracking-widest">{t('products.subtitle')}</p>
                </div>
                {isAdmin && (
                    <button
                        onClick={handleOpenAddModal}
                        className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-black text-white px-5 py-2.5 md:px-6 md:py-3 rounded-xl md:rounded-2xl font-bold transition-all shadow-lg shadow-gray-200 text-xs md:text-base w-full md:w-auto"
                    >
                        <Plus size={18} className="md:w-5 md:h-5" />
                        {t('products.actions.add')}
                    </button>
                )}
            </div>

            <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 md:p-6 border-b border-gray-50 bg-gray-50/30 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 md:w-4.5 md:h-4.5" size={16} />
                        <input
                            type="text"
                            placeholder={t('products.placeholders.search')}
                            className="w-full pl-11 pr-4 py-2.5 md:py-3 bg-white border border-gray-100 rounded-xl md:rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-xs md:text-sm transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 text-[9px] md:text-xs font-bold text-gray-400 uppercase tracking-widest px-1 sm:px-0">
                        <span>{t('products.stats.total', { count: filteredProducts.length })}</span>
                    </div>
                </div>

                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('products.table.product')}</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('products.table.slug')}</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('products.table.price')}</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">{t('common.status')}</th>
                                {isAdmin && <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">{t('common.actions')}</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
                                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">{t('products.messages.loading')}</p>
                                    </td>
                                </tr>
                            ) : filteredProducts.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center">
                                        <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                            <Package className="text-gray-300" size={32} />
                                        </div>
                                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">{t('products.messages.no_data')}</p>
                                    </td>
                                </tr>
                            ) : filteredProducts.map((product) => (
                                <tr key={product._id} className="hover:bg-gray-50/50 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            {product.image ? (
                                                <img
                                                    src={product.image}
                                                    alt={product.name}
                                                    className="w-10 h-10 rounded-xl object-cover border border-gray-100"
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                />
                                            ) : null}
                                            <div
                                                className="w-10 h-10 bg-indigo-50 rounded-xl items-center justify-center text-indigo-600 font-black"
                                                style={{ display: product.image ? 'none' : 'flex' }}
                                            >
                                                {product.name.charAt(0)}
                                            </div>
                                            <div>
                                                <span className="font-bold text-gray-900 block">{product.name}</span>
                                                {product.sourceUrl && (
                                                    <a
                                                        href={product.sourceUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-[10px] text-indigo-400 hover:text-indigo-600 flex items-center gap-1 mt-0.5"
                                                    >
                                                        <Link size={10} /> Lien produit
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="font-mono text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded-md">
                                            {product.slug || '—'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="font-black text-gray-700">
                                            {product.defaultPrice ? `${product.defaultPrice} ${t('common.currency')}` : '—'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        {isAdmin ? (
                                            <button
                                                onClick={() => toggleStatus(product)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${product.active ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}
                                            >
                                                <div className={`w-1.5 h-1.5 rounded-full ${product.active ? 'bg-green-500' : 'bg-red-500'}`} />
                                                {product.active ? t('common.status_map.active') : t('common.status_map.inactive')}
                                            </button>
                                        ) : (
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${product.active ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${product.active ? 'bg-green-500' : 'bg-red-500'}`} />
                                                {product.active ? t('common.status_map.active') : t('common.status_map.inactive')}
                                            </span>
                                        )}
                                    </td>
                                    {isAdmin && (
                                        <td className="px-6 py-5 text-right">
                                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleOpenEditModal(product)}
                                                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProduct(product._id)}
                                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Grid View */}
                <div className="md:hidden divide-y divide-gray-50">
                    {loading ? (
                        <div className="p-12 text-center">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" />
                            <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">{t('products.messages.loading')}</p>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="p-12 text-center">
                            <Package className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                            <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">{t('products.messages.no_data')}</p>
                        </div>
                    ) : filteredProducts.map((product) => (
                        <div key={product._id} className="p-4 space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100/50 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                    {product.image ? (
                                        <img src={product.image} className="w-full h-full object-cover" />
                                    ) : (
                                        <Package className="w-6 h-6 text-gray-300" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 py-0.5">
                                    <h3 className="font-bold text-gray-900 text-sm break-words leading-tight mb-1">{product.name}</h3>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-mono text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded uppercase">{product.slug || '—'}</span>
                                        <span className="font-black text-indigo-600 text-xs">{product.defaultPrice ? `${product.defaultPrice} ${t('common.currency')}` : '—'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <button
                                    onClick={() => toggleStatus(product)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${product.active ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}
                                >
                                    <div className={`w-1.5 h-1.5 rounded-full ${product.active ? 'bg-green-500' : 'bg-red-500'}`} />
                                    {product.active ? t('common.status_map.active') : t('common.status_map.inactive')}
                                </button>

                                {isAdmin && (
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleOpenEditModal(product)}
                                            className="p-2.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                                        >
                                            <Edit3 size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteProduct(product._id)}
                                            className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Product Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-[2rem] w-full max-w-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-5 md:p-6 border-b border-gray-50 flex justify-between items-center bg-white sticky top-0 z-10">
                            <h2 className="text-lg md:text-xl font-black text-gray-900 tracking-tight">
                                {editingProduct ? t('products.actions.edit') : t('products.actions.add_new')}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <X size={20} className="text-gray-400" />
                            </button>
                        </div>
                        <form onSubmit={handleFormSubmit} className="p-5 md:p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
                            {error && (
                                <div className="bg-red-50 text-red-600 p-3 md:p-4 rounded-xl text-[10px] md:text-xs font-bold uppercase tracking-widest border border-red-100 flex items-center gap-2">
                                    <AlertCircle size={14} />
                                    {error}
                                </div>
                            )}
                            <div>
                                <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5 block ml-1">{t('products.form.name')}</label>
                                <input
                                    required
                                    type="text"
                                    className="w-full px-4 py-2.5 md:py-3 bg-gray-50 border border-gray-100 rounded-xl md:rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-xs md:text-sm transition-all"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder={t('products.form.placeholders.name')}
                                />
                            </div>

                            <div>
                                <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5 block ml-1">Lien du produit</label>
                                <div className="relative">
                                    <Link className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                                    <input
                                        type="url"
                                        className="w-full pl-10 pr-4 py-2.5 md:py-3 bg-gray-50 border border-gray-100 rounded-xl md:rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-xs md:text-sm transition-all"
                                        value={formData.sourceUrl}
                                        onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
                                        placeholder="https://azen.ma/products/..."
                                    />
                                </div>
                                {formData.sourceUrl && (
                                    <p className="text-[9px] md:text-[10px] text-indigo-400 mt-1 ml-1">✓ L'image sera chargée automatiquement</p>
                                )}
                            </div>

                            <div>
                                <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5 block ml-1">{t('products.form.default_price')}</label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2.5 md:py-3 bg-gray-50 border border-gray-100 rounded-xl md:rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-xs md:text-sm transition-all"
                                    value={formData.defaultPrice}
                                    onChange={(e) => setFormData({ ...formData, defaultPrice: e.target.value })}
                                    placeholder={t('products.form.placeholders.price')}
                                />
                            </div>

                            <div>
                                <label className="text-[9px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 md:mb-1.5 block ml-1">URL de l'image (Optionnel)</label>
                                <div className="relative">
                                    <Package className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                                    <input
                                        type="url"
                                        className="w-full pl-10 pr-4 py-2.5 md:py-3 bg-gray-50 border border-gray-100 rounded-xl md:rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-xs md:text-sm transition-all"
                                        value={formData.image}
                                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                                        placeholder="https://..."
                                    />
                                </div>
                                <p className="text-[8px] md:text-[9px] text-gray-400 mt-1 ml-1 font-medium italic">Si vide, le système tentera de la récupérer via le lien produit</p>
                            </div>

                            <div className="pt-4 flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="order-2 sm:order-1 flex-1 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold text-gray-400 hover:bg-gray-50 transition-all uppercase tracking-widest text-[10px]"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="order-1 sm:order-2 flex-1 py-3 md:py-4 bg-gray-900 hover:bg-black text-white rounded-xl md:rounded-2xl font-black transition-all shadow-lg shadow-gray-200 flex items-center justify-center gap-2 uppercase tracking-widest text-[10px] disabled:opacity-50"
                                >
                                    {submitting ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                                    {editingProduct ? t('products.actions.save') : t('products.actions.create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Toasts */}
            <div className="fixed top-6 right-6 z-[110] flex flex-col gap-3">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl border animate-in slide-in-from-right-full duration-300 ${toast.type === 'success'
                            ? 'bg-white border-green-100 text-gray-900'
                            : 'bg-white border-red-100 text-gray-900'
                            }`}
                    >
                        {toast.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                            <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="font-semibold text-sm">{toast.message}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Products;
